import { useEffect, useRef } from "react";
import { useBlocker } from "react-router-dom";
import { isAuthRoute } from "utils/isAuthRoute";
import { screenplaySaveGate } from "./screenplaySaveGate";

/**
 * Holds every navigation away from the screenplay editor's page (links, the switchers, the
 * browser's back button) while the editor has an edit waiting to save. The edit is saved
 * first; the navigation goes ahead only once it is saved, to the last place asked for. A
 * failed or hung save keeps the user on the page with the unsaved text, and says so.
 *
 * Signing in or out is never held: the session is already gone, so the save could not land.
 * Mounted once, at the root of the app's data router (see `router.tsx`).
 */
export function ScreenplayLeaveBlocker() {
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      currentLocation.pathname !== nextLocation.pathname &&
      !isAuthRoute(nextLocation.pathname) &&
      screenplaySaveGate.hasUnsavedEdits()
  );
  // A link clicked while the save runs blocks again with its own location, so the latest
  // blocker decides where to go.
  const blockerRef = useRef(blocker);
  blockerRef.current = blocker;
  const isBlocked = blocker.state === "blocked";

  useEffect(() => {
    if (!isBlocked) return undefined;
    const wait = screenplaySaveGate.saveBefore();
    void wait.outcome.then((outcome) => {
      const current = blockerRef.current;
      if (current.state !== "blocked") return;
      if (outcome === "proceed") current.proceed();
      else current.reset();
    });
    return wait.stop;
  }, [isBlocked]);

  return null;
}
